import { AppProvider, useApp } from '@/contexts';

import { AppShell } from '@components/layout';
import { RumWorkspaceAttributes } from '@components/observability';
import { useGetTenantQuery } from '@features/tenants';
import { Alert, Box } from '@mui/material';
import { Outlet, useParams } from 'react-router';

import { AccessDenied } from '@/components/feedback/AccessDenied';
import { isForbiddenError } from '@/utils/apiErrors';

/**
 * Workspace layout wrapper with AppShell
 * Provides sidebar navigation for app-specific pages
 * AppProvider now handles app data fetching and provides appId, tenantId, and app via context
 */
export const WorkspaceLayout = () => {
  const { appId } = useParams<{ appId: string }>();

  if (!appId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No workspace ID provided</Alert>
      </Box>
    );
  }

  return (
    <AppProvider>
      <WorkspaceLayoutInner />
    </AppProvider>
  );
};

/**
 * Inner component that accesses app data from context
 * Must be inside AppProvider to use useApp hook
 */
const WorkspaceLayoutInner = () => {
  const { appId, tenantId, app } = useApp();

  const { data: tenant, error: tenantError } = useGetTenantQuery(tenantId, {
    skip: !tenantId,
  });

  if (tenantError && isForbiddenError(tenantError)) {
    return <AccessDenied />;
  }

  // BUGFIX: Backend returns tenant as single object, but sometimes it's wrapped in an array
  const tenantData = Array.isArray(tenant) ? tenant[0] : tenant;

  return (
    <AppShell
      appId={appId}
      tenantId={tenantId}
      appName={app.name}
      tenantName={app.tenantName || tenantData?.name}
    >
      <RumWorkspaceAttributes />
      <Outlet />
    </AppShell>
  );
};
